import React from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import ClustersTopBar from './components/ClustersTopBar';
import ClustersRoutes from './ClustersRoutes';
import { ClustersMarketProvider } from './contexts/ClustersMarketContext';

// deepcontext-l8re: the Universe overview route drops the outer Card so its
// own controls row + chart card sit directly on the page canvas.
// deepcontext-6l96.2: Top Mentions joins it — its bubble canvas already
// draws its own bordered panel (getMapPanelStyle), so wrapping that in the
// generic subpage Card double-framed it (a canvas-shaped border inside
// another Card-shaped border). The remaining sub-pages (Trending, My Briefs,
// deepcontext-ctk6: Trending joins them — its header moved into AppLayout's
// shared page-header row (eyebrow + title left, stat trio right), leaving the
// generic subpage Card as a container wrapped around containers.
// deepcontext-d0i8: URL Explorer joins them too — its title/subtitle and
// second URL pill are gone, market/search/cluster controls moved into the
// shared header row, and the table sits directly on the canvas. My Briefs is
// the only sub-page still wrapped in the generic Card.
const FULL_BLEED_PATHS = /^\/clusters\/(overview|top-mentions|trending|url-explorer)\/?$/;

/**
 * Page shell for the Clusters section (mounted at `/clusters/*`). Renders the
 * market top bar and the nested route outlet.
 *
 * The in-page horizontal tab strip that used to switch between the 5 views was
 * removed (bd-deepcontext-zuqg0); those views are now reached from the sidebar
 * sub-nav under the "Clusters" item. Removing the strip reclaims a control row
 * for a taller bubble graph.
 *
 * The ClustersMarketProvider still wraps every sub-page so the selected market
 * persists across sidebar navigation and survives reload (localStorage key
 * `clusters.selectedMarket`, unchanged).
 *
 * deepcontext-0ihe: `ClustersTopBar` no longer renders a standalone bordered
 * bar — it portals the market selector + URL-count badge into AppLayout's
 * `app-page-actions` slot, onto the same row as the muted page subtitle (the
 * "no title" actions-row pattern). It renders nothing in this tree itself, so
 * it's mounted as a bare sibling rather than wrapped in a layout div.
 *
 * Uses host UI primitives (Card / Button / Badge) only.
 */
const Clusters: React.FC = () => {
    const location = useLocation();
    const isFullBleed = FULL_BLEED_PATHS.test(location.pathname);

    return (
        <ClustersMarketProvider>
            <ClustersTopBar />
            {/*
             * Height chain (bd-deepcontext-vyptc): AppLayout hands this shell a
             * definite-height children wrapper (h-full inside a flex-col main).
             * This shell must PROPAGATE that height down to the bubble graph,
             * otherwise `h-full` deep inside ClustersPage resolves against a
             * content-sized ancestor and the page leaves gray dead-space below
             * the card. Two earlier fixes (#306, #310) patched ClustersPage's
             * OWN grid/flex but the break was here: this wrapper, the Card and
             * CardContent were all content-height, severing the chain above
             * ClustersPage. Make the column fill the viewport (h-full + flex-col
             * + min-h-0), pin the top bar, and let the Card take the rest.
             *
             * deepcontext-l8re: the overview route propagates that same
             * height chain WITHOUT the Card/CardContent — its own controls row
             * + chart card sit directly on the page canvas (page lock option
             * B). deepcontext-6l96.2: Top Mentions joins it for the same
             * reason — its own bubble canvas is already a bordered panel, so
             * it doesn't need a second one wrapped around it. The remaining
             * sub-pages are untouched by this bead, so they keep the Card
             * wrapper exactly as before.
             */}
            {isFullBleed ? (
                <div
                    className="flex flex-col h-full min-h-0"
                    data-testid="clusters-full-bleed-shell"
                >
                    <ClustersRoutes />
                </div>
            ) : (
                <div
                    className="flex flex-col h-full min-h-0 gap-4"
                    data-testid="clusters-subpage-shell"
                >
                    <Card className="flex-1 min-h-0 flex flex-col">
                        <CardContent className="px-4 py-4 flex-1 min-h-0 flex flex-col overflow-auto">
                            <ClustersRoutes />
                        </CardContent>
                    </Card>
                </div>
            )}
        </ClustersMarketProvider>
    );
};

export default Clusters;
